"use client";

import { useState, useEffect, useTransition } from "react";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import { RegionalAirportConfig } from "./types";
import { type Locale } from "@/i18n/config";
import { pickLocalized } from "./types";
import TopBar from "@/ui/layout/header/topBar";

interface RegionalNavProps {
  config: RegionalAirportConfig;
}

export default function RegionalNav({ config }: RegionalNavProps) {
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Detectar scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Cambiar idioma
  const handleLocaleChange = (newLocale: string) => {
    if (newLocale === locale) return;
    const newPath = pathname.replace(`/${locale}`, `/${newLocale}`);
    startTransition(() => {
      router.push(newPath);
    });
    setIsMobileMenuOpen(false);
  };

  // El menú móvil abierto siempre usa la variante sólida para legibilidad
  const solid = isScrolled || isMobileMenuOpen;

  const isActiveLink = (href: string) => {
    const fullPath = `/${locale}${href}`;
    return pathname === fullPath;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-[#102542] border-b border-white/10"
          : "bg-black/20 backdrop-blur-md"
      }`}
    >
      {/* Misma barra de portales que el sitio principal; se retrae al bajar */}
      <TopBar isScrolled={isScrolled} />

      <div className="max-w-[90%] mx-auto">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo / Nombre */}
          <Link
            href={`/${locale}${config.basePath}`}
            className="flex items-baseline gap-3 min-w-0"
          >
            <span className="text-lg md:text-xl font-bold tracking-tight text-white">
              {config.code}
            </span>
            <span className="hidden sm:block text-xs truncate text-white/70">
              {pickLocalized(config.name, locale)}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {config.navLinks.map((link, index) => (
              <Link
                key={index}
                href={`/${locale}${link.href}`}
                className={`text-sm transition-colors ${
                  isActiveLink(link.href)
                    ? "text-white font-semibold"
                    : "text-white/70 font-medium hover:text-white"
                }`}
              >
                {pickLocalized(link.label, locale)}
              </Link>
            ))}
          </div>

          {/* Right Section: Language Switch + Mobile Menu Button */}
          <div className="flex items-center gap-3">
            {/* Language Switcher: desde lg el del topbar ocupa su lugar */}
            <div className="flex lg:hidden items-center rounded-full border border-white/40">
              {(["es", "en"] as const).map((lang) => (
                <button
                  key={lang}
                  onClick={() => handleLocaleChange(lang)}
                  disabled={isPending}
                  className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-semibold transition-colors cursor-pointer ${
                    locale === lang
                      ? "bg-white text-[#102542]"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-white hover:text-white/80 transition-colors cursor-pointer"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ease-out ${
          isMobileMenuOpen
            ? "max-h-100 opacity-100 border-t border-white/10"
            : "max-h-0 opacity-0"
        }`}
      >
        <div className="max-w-[90%] mx-auto py-4 flex flex-col">
          {config.navLinks.map((link, index) => (
            <Link
              key={index}
              href={`/${locale}${link.href}`}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`py-3 text-sm border-b border-white/10 last:border-b-0 transition-colors ${
                isActiveLink(link.href)
                  ? "text-white font-semibold"
                  : "text-white/70 font-medium hover:text-white"
              }`}
            >
              {pickLocalized(link.label, locale)}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}
